;(function () {
    const SWATCH = "[data-fabric-swatch]"
    const SECTION = "#main-product-section"
    const TRUST_BAR = "[data-product-trust-bar]"
    const DRAWER = "product-size-chart-drawer"
    const GALLERY = "product-gallery-marquee"
    const VARIANT_SELECTOR = "product-card-variant-selector"
    const INFO_COLUMN = "[data-product-info]"
    const ACCORDION_ITEM = "[data-product-accordions] .accordion"
    const ANNOUNCER = "[data-fabric-announcer]"
    const RERENDER = "[fabric-swap-render]"
    const JUNIP_WIDGET = ".junip-product-summary, .junip-product-review"

    const LOADING_CLASS = "is-fabric-loading"
    const SWAPPING_CLASS = "is-fabric-swapping"

    const LOADING_DELAY_MS = 250
    const PREFETCH_DELAY_MS = 65
    const DECODE_TIMEOUT_MS = 500
    const FADE_OUT_MS = 160
    const FADE_IN_MS = 240
    const WARM_IMAGE_COUNT = 3
    const JUNIP_INIT_GRACE_MS = 1000
    const CACHE_LIMIT = 8 

    const parser = new DOMParser()
    const documentCache = new Map()

    let currentPath = window.location.pathname
    let requestToken = 0
    let prefetchTimer = null
    let prefetchTarget = null

    const wait = (ms) =>
        new Promise((resolve) => window.setTimeout(resolve, ms))

    const prefersReducedMotion = () =>
        window.matchMedia("(prefers-reduced-motion: reduce)").matches

    function cacheKey(url) {
        return new URL(url, window.location.origin).pathname
    }

    function fetchDocument(url) {
        const key = cacheKey(url)
        const cached = documentCache.get(key)

        if (cached) {
            documentCache.delete(key)
            documentCache.set(key, cached)
            return cached
        }

        const request = fetch(url, { credentials: "same-origin" })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(
                        `Fabric swap: ${response.status} fetching ${url}`
                    )
                }
                return response.text()
            })
            .then((html) => parser.parseFromString(html, "text/html"))
            .catch((error) => {
                documentCache.delete(key) // never cache a failure
                throw error
            })

        if (documentCache.size >= CACHE_LIMIT) {
            documentCache.delete(documentCache.keys().next().value) // least recently used out first
        }
        documentCache.set(key, request)

        return request
    }

    function prefetch(url) {
        if (documentCache.has(cacheKey(url))) return 

        fetchDocument(url).catch(() => {})
    }

    function warmImages(doc) {
        const sources = Array.from(
            doc.querySelectorAll(`${GALLERY} img`)
        ).slice(0, WARM_IMAGE_COUNT)
        if (!sources.length) return Promise.resolve()

        const decoded = sources.map((source) => {
            const image = new Image()

            if (source.sizes) image.sizes = source.sizes
            if (source.srcset) image.srcset = source.srcset
            image.src = source.src
            return image.decode().catch(() => {})
        })

        return Promise.race([Promise.all(decoded), wait(DECODE_TIMEOUT_MS)])
    }

    function captureSelectedOptions() {
        const selector = document.querySelector(VARIANT_SELECTOR)
        if (!selector) return []

        return Array.from(selector.querySelectorAll("[data-option-value]"))
            .filter((el) =>
                el.tagName === "INPUT"
                    ? el.checked
                    : el.classList.contains("selected")
            )
            .map((el) => ({
                type: el.dataset.optionType,
                value: el.dataset.optionValue,
            }))
    }

    function captureState() {
        const gallery = document.querySelector(GALLERY)
        const infoColumn = document.querySelector(`${SECTION} ${INFO_COLUMN}`)
        const items = Array.from(document.querySelectorAll(ACCORDION_ITEM))

        return {
            options: captureSelectedOptions(),
            
            galleryIndex: gallery?.emblaApi
                ? gallery.emblaApi.selectedScrollSnap()
                : null,
            // Several accordions can be open at once, so carry every index.
            openAccordions: items.reduce((open, item, index) => {
                if (item.hasAttribute("open")) open.push(index)
                return open
            }, []),
            infoScrollTop: infoColumn ? infoColumn.scrollTop : 0,
        }
    }

    function restoreSelectedOptions(options) {
        if (!options.length) return

        const selector = document.querySelector(VARIANT_SELECTOR)
        if (!selector) return

        options.forEach(({ type, value }) => {
            if (!type || !value) return
            const match = selector.querySelector(
                `[data-option-value="${CSS.escape(value)}"][data-option-type="${CSS.escape(type)}"]`
            )
            if (match) match.click()
        })
    }

    // Set the attribute directly rather than clicking the header: the click
    // handler toggles, so a header that already came back open would close.
    function restoreOpenAccordions(indices) {
        if (!indices || !indices.length) return
        const items = document.querySelectorAll(ACCORDION_ITEM)
        indices.forEach((index) => {
            const item = items[index]
            if (item) item.setAttribute("open", "")
        })
    }

    // ---------- DOM application ----------
    function replaceFrom(doc, selector) {
        const incoming = doc.querySelector(selector)
        const outgoing = document.querySelector(selector)
        if (!incoming || !outgoing) return null

        const clone = incoming.cloneNode(true)
        outgoing.replaceWith(clone)
        return clone
    }

    function syncMeta(selector, attribute, doc) {
        const incoming = doc.head.querySelector(selector)
        if (!incoming) return

        const existing = document.head.querySelector(selector)
        if (existing) {
            existing.setAttribute(
                attribute,
                incoming.getAttribute(attribute) || ""
            )
        } else {
            document.head.appendChild(incoming.cloneNode(true))
        }
    }

    function syncHead(doc) {
        document.title = doc.title

        syncMeta('link[rel="canonical"]', "href", doc)
        syncMeta('meta[name="description"]', "content", doc)

        doc.head
            .querySelectorAll('meta[property^="og:"], meta[name^="twitter:"]')
            .forEach((incoming) => {
                const property = incoming.getAttribute("property")
                const key = property
                    ? `meta[property="${property}"]`
                    : `meta[name="${incoming.getAttribute("name")}"]`
                const existing = document.head.querySelector(key)

                if (existing) {
                    existing.setAttribute(
                        "content",
                        incoming.getAttribute("content") || ""
                    )
                } else {
                    document.head.appendChild(incoming.cloneNode(true))
                }
            })

        document.head
            .querySelectorAll('script[type="application/ld+json"]')
            .forEach((node) => node.remove())
        doc.head
            .querySelectorAll('script[type="application/ld+json"]')
            .forEach((node) => document.head.appendChild(node.cloneNode(true)))
    }

    function replaceOptedInSections(doc) {
        const replaced = new Set()

        document.querySelectorAll(RERENDER).forEach((marked) => {
            const outgoing = marked.closest(".shopify-section") || marked

            if (outgoing.matches(SECTION) || outgoing.querySelector(SECTION)) {
                return
            }

            const id = outgoing.id

            if (!id || replaced.has(id)) return

            const incoming = doc.getElementById(id)
            if (!incoming) return

            replaced.add(id)
            outgoing.replaceWith(incoming.cloneNode(true))
        })
    }

    function replaceDrawer(doc) {
        const outgoing = document.querySelector(DRAWER)

        if (
            outgoing &&
            outgoing.classList.contains("open") &&
            typeof outgoing.close === "function"
        ) {
            outgoing.close()
        }
        replaceFrom(doc, DRAWER)
    }

    function applyDom(doc, state, startHidden) {
        const incoming = doc.querySelector(SECTION)
        const outgoing = document.querySelector(SECTION)
        if (!incoming || !outgoing) {
            throw new Error("Fabric swap: no product section in the response")
        }

        const section = incoming.cloneNode(true)
        if (startHidden) section.classList.add(SWAPPING_CLASS)

        if (state.galleryIndex !== null) {
            const gallery = section.querySelector(GALLERY)
            if (gallery) {
                gallery.dataset.initialIndex = String(state.galleryIndex)
            }
        }

        outgoing.replaceWith(section)
        replaceFrom(doc, TRUST_BAR)

        replaceOptedInSections(doc)
        replaceDrawer(doc)
        syncHead(doc)

        return section
    }

    // ---------- post-swap re-initialisation ----------
    function refreshJunipWidgets() {
        window.setTimeout(() => {
            if (typeof window.junip?.init !== "function") return

            const pending = Array.from(
                document.querySelectorAll(JUNIP_WIDGET)
            ).some((widget) => widget.dataset.initialized !== "true")

            if (!pending) return

            try {
                window.junip.init()
            } catch (error) {
                console.error(error)
            }
        }, JUNIP_INIT_GRACE_MS)
    }

    function announce(name) {
        const announcer = document.querySelector(ANNOUNCER)
        if (announcer && name) announcer.textContent = name
    }

    function finalise(state, name) {
        window.ProductVariantGuard?.suppressDefaultSelection()
        restoreSelectedOptions(state.options)

        restoreOpenAccordions(state.openAccordions)

        const infoColumn = document.querySelector(`${SECTION} ${INFO_COLUMN}`)
        if (infoColumn) infoColumn.scrollTop = state.infoScrollTop

        refreshJunipWidgets()
        announce(name)

        document.dispatchEvent(
            new CustomEvent("fabric:swapped", {
                detail: { url: window.location.href, path: currentPath },
            })
        )
    }

    // ---------- animation ----------
    function runSwap(mutate, isCurrent) {
        if (prefersReducedMotion()) {
            mutate(false)
            return Promise.resolve()
        }

        if (typeof document.startViewTransition === "function") {
            const transition = document.startViewTransition(() => mutate(false))

            transition.finished.catch(() => {})
            return transition.updateCallbackDone
        }

        const outgoing = document.querySelector(SECTION)
        if (!outgoing) {
            mutate(false)
            return Promise.resolve()
        }

        outgoing.classList.add(SWAPPING_CLASS)

        return wait(FADE_OUT_MS).then(() => {
            if (!isCurrent()) return

            const section = mutate(true)
            if (!section) return

            void section.offsetWidth 
            section.classList.remove(SWAPPING_CLASS)
            return wait(FADE_IN_MS)
        })
    }

    // ---------- orchestration ----------
    async function swapTo(url, { push = true, name } = {}) {
        const token = ++requestToken
        const isCurrent = () => token === requestToken

        const loadingTimer = window.setTimeout(() => {
            if (isCurrent()) {
                document.querySelector(SECTION)?.classList.add(LOADING_CLASS)
            }
        }, LOADING_DELAY_MS)

        try {
            const state = captureState()

            const doc = await fetchDocument(url)
            if (!isCurrent()) return

            await warmImages(doc)
            if (!isCurrent()) return

            if (push) window.history.pushState({}, "", url)
            currentPath = new URL(url, window.location.origin).pathname

            await runSwap((startHidden) => {
                const section = applyDom(doc, state, startHidden)
                requestAnimationFrame(() => finalise(state, name))
                return section
            }, isCurrent)
        } catch (error) {
            console.error(error)
            window.location.href = url 
        } finally {
            window.clearTimeout(loadingTimer)

            if (isCurrent()) {
                document.querySelector(SECTION)?.classList.remove(LOADING_CLASS)
            }
        }
    }

    // ---------- events ----------
    function setActiveSwatch(link) {
        document.querySelectorAll(SWATCH).forEach((swatch) => {
            swatch.setAttribute(
                "aria-current",
                swatch === link ? "true" : "false"
            )
        })
    }

    function prefetchCandidate(event) {
        const link = event.target?.closest?.(SWATCH)
        if (!link || link.getAttribute("aria-current") === "true") return null
        return link
    }

    function onPrefetchIntent(event) {
        const link = prefetchCandidate(event)
        if (!link || link === prefetchTarget) return

        prefetchTarget = link
        window.clearTimeout(prefetchTimer)
        prefetchTimer = window.setTimeout(
            () => prefetch(link.href),
            PREFETCH_DELAY_MS
        )
    }

    function onTouchIntent(event) {
        const link = prefetchCandidate(event)
        if (link) prefetch(link.href)
    }

    function onClick(event) {
        if (event.defaultPrevented) return
        if (
            event.button !== 0 ||
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey
        ) {
            return // let the browser open a new tab / download as asked
        }

        const link = event.target?.closest?.(SWATCH)
        if (!link) return

        event.preventDefault()
        if (link.getAttribute("aria-current") === "true") return

        window.clearTimeout(prefetchTimer)
        prefetchTarget = null

        setActiveSwatch(link)
        swapTo(link.href, { push: true, name: link.dataset.fabricName })
    }

    function onPopState() {
        const path = window.location.pathname
        
        if (path === currentPath) return

        if (!path.includes("/products/")) {
            window.location.reload()
            return
        }

        const handle = path.split("/products/")[1]?.split(/[/?#]/)[0]
        const target =
            handle &&
            document.querySelector(
                `[data-fabric-handle="${CSS.escape(handle)}"]`
            )
        if (target) setActiveSwatch(target)

        swapTo(window.location.href, {
            push: false,
            name: target?.dataset.fabricName,
        })
    }

    function init() {
        if (!document.querySelector(SECTION)) return

        currentPath = window.location.pathname

        document.addEventListener("pointerover", onPrefetchIntent)
        document.addEventListener("focusin", onPrefetchIntent)
        document.addEventListener("touchstart", onTouchIntent, {
            passive: true,
        })
        document.addEventListener("click", onClick)
        window.addEventListener("popstate", onPopState)
    }

    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", init)
    } else {
        init()
    }
})()
